/**
 * GrowthFutureIntegrations — Premium locked feature cards (coming soon)
 */
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Zap } from "lucide-react";

const integrations = [
  {
    name: "Meta Ads",
    icon: "📘",
    description: "Campanhas, gasto, ROAS, impressões, cliques",
    color: "from-blue-500 to-blue-600"
  },
  {
    name: "Google Ads",
    icon: "🔍",
    description: "Google Ads, Google Analytics 4, conversões",
    color: "from-red-500 to-orange-500"
  },
  {
    name: "Instagram",
    icon: "📷",
    description: "Instagram Ads, Stories, Reels performance",
    color: "from-pink-500 to-purple-600"
  },
  {
    name: "ROAS",
    icon: "💰",
    description: "Return on Ad Spend por campanha",
    color: "from-green-500 to-emerald-600"
  },
  {
    name: "ROI",
    icon: "📈",
    description: "Return on Investment (receita - gasto) / gasto",
    color: "from-indigo-500 to-blue-600"
  },
  {
    name: "CAC",
    icon: "👤",
    description: "Customer Acquisition Cost por channel",
    color: "from-yellow-500 to-orange-600"
  },
  {
    name: "Influenciadores",
    icon: "⭐",
    description: "Performance de campanhas com influenciadores",
    color: "from-purple-500 to-pink-600"
  },
  {
    name: "Atribuição",
    icon: "🔗",
    description: "Multi-touch attribution (first, last, linear)",
    color: "from-cyan-500 to-blue-600"
  }
];

export default function GrowthFutureIntegrations() {
  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 rounded-xl border border-slate-700/50 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              <h2 className="text-2xl font-bold">Integrações Futuras</h2>
            </div>
            <p className="text-slate-300">
              Estes recursos inovadores estão em desenvolvimento e serão lançados em breve.
            </p>
          </div>
          <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center">
            <span className="text-2xl">🚀</span>
          </div>
        </div>
      </div>

      {/* Integration Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {integrations.map((integration) => (
          <Card
            key={integration.name}
            className="relative border-slate-300 hover:border-slate-400 transition-all opacity-80 hover:opacity-100"
          >
            {/* Gradient Top Bar */}
            <div className={`h-1.5 bg-gradient-to-r ${integration.color} rounded-t-lg`} />

            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-3xl">{integration.icon}</span>
                  <CardTitle className="text-sm font-bold text-slate-900">
                    {integration.name}
                  </CardTitle>
                </div>
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                  <Lock className="w-4 h-4 text-slate-600" />
                </div>
              </div>
            </CardHeader>

            <CardContent className="pb-4">
              <p className="text-xs text-slate-600 leading-relaxed">
                {integration.description}
              </p>
              <div className="mt-4 pt-3 border-t border-slate-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                  🔒 Em breve
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Roadmap Card */}
      <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-xl">📅</span>
            Roadmap de Lançamento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm">1</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Atribuição (Fase 2)</p>
              <p className="text-xs text-slate-600">Capturar fbclid, utm_source e utm_medium</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm">2</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Meta Ads Sync (Fase 3)</p>
              <p className="text-xs text-slate-600">Integração OAuth, spend tracking, ROAS</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm">3</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">IA Recommendations (Fase 4)</p>
              <p className="text-xs text-slate-600">Alertas inteligentes e otimizações automáticas</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold text-sm">4</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Mais Integrações (Fase 5+)</p>
              <p className="text-xs text-slate-600">Google Ads, Instagram, TikTok, atribuição avançada</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Support Card */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="pt-6">
          <p className="text-sm text-amber-900">
            💡 <strong>Precisa de uma integração específica?</strong> Entre em contato com nosso time de suporte
            e ajudaremos a priorizar essa funcionalidade na nossa roadmap.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
